import * as React from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { Input, Select } from "@jhool-io/fe-components";
import {
    isAcceptJSValidationError,
    useAcceptJS,
} from "../../../../hooks/useAcceptJS/useAcceptJS";
import { useAddPaymentCard } from "../../../billing/hooks/billing.mutations";
import { IAddPaymentCardPayload } from "../../../billing/types/billing.types";
import { IAuthorizeNetCredentials } from "../../../auth/types/auth.types";
import useToast from "../../../../hooks/useToast";
import { STATES } from "../../../../utils/helpers/us-states/us-states";

interface AddCardResponse {
    message?: string;
}

export interface AddCardFormProps {
    onClose?: () => void;
    onSaved?: () => void | Promise<void>;
    formId?: string;
    consentId?: string;
    authorizeNetCredentials?: IAuthorizeNetCredentials;
    useDefaultCredentials?: boolean;
    addPaymentCard?: (
        payload: IAddPaymentCardPayload
    ) => Promise<AddCardResponse>;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

const schema = yup.object({
    cardholder_name: yup.string().required("Cardholder name is required"),
    card_number: yup.string().required("Card number is required"),
    expiration_date: yup
        .string()
        .matches(/^\d{2}\/\d{2}$/, "Expiration date must be in MM/YY format")
        .required("Expiration date is required"),
    card_cvv: yup
        .string()
        .min(3, "CVV must be at least 3 characters")
        .max(4, "CVV must be at most 4 characters")
        .matches(/^\d+$/, "CVV must contain only numbers")
        .required("Card CVV is required"),
    billing_address: yup.string().required("Billing address is required"),
    zip_code: yup.string().required("Zip code is required"),
    state: yup.string().required("State is required"),
    country: yup.string().required("Country is required"),
});

type AddCardFormValues = yup.InferType<typeof schema>;

const stateOptions = STATES.map((state) => ({
    label: state.name,
    value: state.abbrv,
}));

export default function AddCardForm({
    onClose,
    onSaved,
    formId = "add-card-form",
    consentId,
    authorizeNetCredentials,
    useDefaultCredentials = true,
    addPaymentCard,
}: AddCardFormProps) {
    const { tokenizeCard } = useAcceptJS({
        credentials: authorizeNetCredentials,
        useDefaultCredentials,
    });
    const { mutateAsync: addCard } = useAddPaymentCard(consentId);
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const submittingRef = React.useRef(false);

    const {
        control,
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<AddCardFormValues>({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            country: "USA",
        },
    });

    const extractFirstName = (fullName = "") =>
        fullName.trim().split(" ").slice(0, -1).join(" ") || fullName.trim();

    const extractLastName = (fullName = "") =>
        fullName.trim().split(" ").slice(-1)[0] || "";

    const setAcceptJSCardErrors = (error: unknown) => {
        if (!isAcceptJSValidationError(error)) {
            return;
        }

        error.issues.forEach((issue) => {
            switch (issue.field) {
                case "card_number":
                    setError("card_number", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "expiration_date":
                    setError("expiration_date", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "card_cvv":
                    setError("card_cvv", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "zip_code":
                    setError("zip_code", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                case "cardholder_name":
                    setError("cardholder_name", {
                        type: "validate",
                        message: issue.message,
                    });
                    break;

                default:
                    toast({ mode: "error", message: issue.message });
            }
        });
    };

    const onSubmit = async (payload: AddCardFormValues) => {
        if (submittingRef.current) return;
        submittingRef.current = true;

        try {
            let tokenResult: Awaited<ReturnType<typeof tokenizeCard>>;
            const [expirationMonth = "", expirationYear = ""] =
                payload.expiration_date.split("/");

            try {
                tokenResult = await tokenizeCard({
                    cardNumber: payload.card_number,
                    cardCode: payload.card_cvv,
                    month: expirationMonth,
                    year: expirationYear ? `20${expirationYear}` : "",
                    fullName: payload.cardholder_name,
                    zip: payload.zip_code,
                });
            } catch (error) {
                setAcceptJSCardErrors(error);
                return;
            }

            const { opaqueData } = tokenResult;
            const request = {
                tokenized_payment: {
                    opaque_data: {
                        dataDescriptor: opaqueData.dataDescriptor,
                        dataValue: opaqueData.dataValue,
                    },
                    billing_info: {
                        first_name: extractFirstName(payload.cardholder_name),
                        last_name: extractLastName(payload.cardholder_name),
                        billing_address: payload.billing_address,
                        state: payload.state,
                        zip_code: payload.zip_code,
                        country: payload.country,
                    },
                },
            };

            const response = addPaymentCard
                ? await addPaymentCard(request)
                : await addCard(request);
            if (!addPaymentCard) {
                await queryClient.invalidateQueries({
                    queryKey: ["payment-cards"],
                });
            }
            toast({
                mode: "success",
                message: response.message || "Payment card added successfully",
            });
            await onSaved?.();
            onClose?.();
        } catch (error) {
            const responseMessage = (
                error as { response?: { data?: { message?: string } } }
            ).response?.data?.message;
            toast({
                mode: "error",
                message:
                    responseMessage ||
                    "Could not add payment card at this time",
            });
        } finally {
            submittingRef.current = false;
        }
    };

    return (
        <form id={formId} onSubmit={handleSubmit(onSubmit)}>
            <div className="fg">
                <Input
                    {...register("cardholder_name")}
                    label="Cardholder Name"
                    placeholder="Name on card"
                    hasError={!!errors.cardholder_name}
                    errorText={errors.cardholder_name?.message}
                    className="fs-exclude"
                    isRequired
                />
            </div>
            <div className="fg">
                <Input
                    {...register("card_number")}
                    label="Card number"
                    placeholder="Card number"
                    hasError={!!errors.card_number}
                    errorText={errors.card_number?.message}
                    className="fs-exclude"
                    isRequired
                />
            </div>
            <div className="fg">
                <Input
                    {...register("expiration_date")}
                    label="Expiry Date"
                    placeholder="Expiration date(mm/yy)"
                    maxLength={5}
                    hasError={!!errors.expiration_date}
                    errorText={errors.expiration_date?.message}
                    className="fs-exclude"
                    isRequired
                />
            </div>
            <div className="fg">
                <Input
                    {...register("card_cvv")}
                    label="Security code (CVV)"
                    placeholder="Security code (CVV)"
                    hasError={!!errors.card_cvv}
                    errorText={errors.card_cvv?.message}
                    className="fs-exclude"
                    isRequired
                />
            </div>
            <div className="fg">
                <Input
                    {...register("billing_address")}
                    label="Billing address (please include city)"
                    placeholder="Billing address (please include city)"
                    hasError={!!errors.billing_address}
                    errorText={errors.billing_address?.message}
                    className="fs-exclude"
                    isRequired
                />
            </div>
            <div className="fg">
                <Input
                    {...register("zip_code")}
                    label="Zip code"
                    placeholder="Zip code"
                    hasError={!!errors.zip_code}
                    errorText={errors.zip_code?.message}
                    className="fs-exclude"
                    isRequired
                />
            </div>
            <div className="fg fg-space-between two flex fs-exclude">
                <Controller
                    name="state"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="State"
                            options={stateOptions}
                            onChange={(val) =>
                                field.onChange((val as Option).value)
                            }
                            isSearchable
                            hasError={!!errors.state}
                            errorText={errors.state?.message}
                            isLongListInDialog
                            isRequired
                        />
                    )}
                />
                <Controller
                    name="country"
                    control={control}
                    render={({ field }) => (
                        <Select
                            label="Country"
                            options={[
                                {
                                    label: "United States",
                                    value: "USA",
                                },
                            ]}
                            onChange={(val) =>
                                field.onChange((val as Option).value)
                            }
                            isSearchable
                            hasError={!!errors.country}
                            errorText={errors.country?.message}
                            defaultValue={{
                                label: "United States",
                                value: "USA",
                            }}
                            isRequired
                        />
                    )}
                />
            </div>
        </form>
    );
}
